import {RootBannerSection} from "./rootBannerSection";

declare const c: any;

export class RootFlashsaleSection {
  /**
   * Set up anchor attribution
   * @param anchor the current <a> tag
   * @param path href
   * @param headline title / tooltip
   * @param mediaId
   */
  private static setupAnchorAttribs(
    anchor: HTMLAnchorElement,
    path: string,
    headline: string | null,
    mediaId: string | null,
  ): void {
    anchor.setAttribute("href", path);

    if (headline !== null) {
      anchor.setAttribute("title", headline);
      anchor.dataset.gaPromoName = headline;
    }

    anchor.dataset.gaPromoMediaid = mediaId !== null ? mediaId : undefined;
  }

  /**
   * Sets data-end-date attribute to end date of flashsale
   * @param anchor current anchor
   * @param endDate end date
   */
  private static setupFlashsaleTimer(anchor: HTMLAnchorElement, endDate?: string): void {
    const timerSpan = anchor.getElementsByClassName("flash-sale__timer")[0] as HTMLSpanElement;
    timerSpan.dataset.endDate = endDate;
  }

  /**
   * Setup of IMG tag with src, alt text, and onload handler
   * which is counting to 3 until it renders swiper
   * @param anchor
   * @param imgSrc
   * @param alt
   * @param imgLoadCounter
   */
  private static setupImgTag(
    anchor: HTMLAnchorElement,
    imgSrc: string | null,
    alt: string | null,
    imgLoadCounter: () => void,
  ): void {
    const img = anchor.getElementsByTagName("img")[0];
    img.setAttribute("src", imgSrc !== null ? imgSrc : "unknown");
    img.setAttribute("alt", alt !== null ? alt : "unknown");

    // If all pics loaded, then start rendering
    img.addEventListener("load", imgLoadCounter);
  }

  // Instances
  private readonly flashsaleSection: HTMLElement;
  private readonly flashsaleData: Document;
  private readonly flashsaleElements: Element[];
  private swiper?: RootBannerSection;

  // ctor
  /**
   * ctor
   * @param flashsaleSection Section element holding flashsale swiper
   * @param flashsaleData Data loaded either from local sessionStorage or by fetch
   */
  constructor(flashsaleSection: HTMLElement, flashsaleData: Document) {
    this.flashsaleSection = flashsaleSection;
    this.flashsaleData = flashsaleData;
    // info: local sortFlashsales can be restored from pr "VER-100376"
    this.flashsaleElements = c.specialOffer.sortFlashsales(this.flashsaleData);
    this.initRender();
  }

  /**
   * Initialize all flashsales before swiper rendering
   */
  private initRender(): void {
    if (this.flashsaleElements.length === 0) {
      const parent = this.flashsaleSection.parentElement;
      if (parent !== null) {
        parent.removeChild(this.flashsaleSection);
      }

      return;
    }

    // Prepare image load counter closure function
    const maximum = this.getMaximumSlots();
    const imgLoadCounter = this.imageLoadCounter(maximum, this.runRender.bind(this));

    // Iterate over flashsales
    const flashsaleSlots = this.flashsaleSection.getElementsByClassName("flash-sale");
    Array.from(flashsaleSlots).forEach((anchor: HTMLAnchorElement, i: number) => {
      if (i < this.flashsaleElements.length) {
        const pathEl = this.flashsaleElements[i].getElementsByTagName("Path")[0];
        if (pathEl !== undefined) {
          // Every values from flashsale data
          const path = pathEl.textContent !== null ? pathEl.textContent : "#";
          const mediaId = this.flashsaleElements[i].getElementsByTagName("MediaId")[0].textContent;
          const headline = this.flashsaleElements[i].getElementsByTagName("Headline")[0].textContent;
          const endDate = this.flashsaleElements[i].getElementsByTagName("EndDate")[0].textContent;
          const imgSrc = this.flashsaleElements[i].getElementsByTagName("BlogCTAImg2x")[0].textContent;
          const alt = this.flashsaleElements[i].getElementsByTagName("ImgAltText")[0].textContent;

          // Set all anchor attributes
          RootFlashsaleSection.setupAnchorAttribs(anchor, path, headline, mediaId);

          // Enddate text preparation
          RootFlashsaleSection.setupFlashsaleTimer(anchor, endDate !== null ? endDate : undefined);

          // H2 headline
          anchor.getElementsByTagName("h2")[0].textContent = headline;

          // Image preparation
          RootFlashsaleSection.setupImgTag(anchor, imgSrc, alt, imgLoadCounter);
        }
      } else {
        const parentDiv = anchor.parentElement;
        if (parentDiv !== null) {
          const parentContainer = parentDiv.parentElement;
          if (parentContainer !== null) {
            parentContainer.removeChild(parentDiv);
          }
        }
      }
    });
  }

  /**
   * Maximum number of slots shown
   */
  private getMaximumSlots(): number {
    const flashsaleSlots = this.flashsaleSection.getElementsByClassName("flash-sale");
    const fsSlotLength = flashsaleSlots.length;
    const fsELemLength = this.flashsaleElements.length;

    return fsELemLength < fsSlotLength ? fsELemLength : fsSlotLength;
  }

  /**
   * Start swiper rendering only when pics are loaded (closure variable preparation)
   * @param maximum number of IMGs to finish loading, before start rendering swiper
   * @param execFunction method containing all necessary init calls, ideally with every call in "this" context
   */
  private imageLoadCounter(maximum: number, execFunction: () => void): () => void {
    let i = 0;
    const count = () => {
      return ++i === maximum;
    };

    return () => {
      if (count()) {
        execFunction();
      }
    };
  }

  /**
   * Initialize Parameters
   */
  private initParams(): any {
    const nextEl = this.flashsaleSection.getElementsByClassName("swiper-button-next")[0];
    const prevEl = this.flashsaleSection.getElementsByClassName("swiper-button-prev")[0];

    return {
      lazy: {
        loadPrevNext: true,
      },
      loop: true,
      navigation: {
        nextEl,
        prevEl,
      },
      preloadImages: false,
      simulateTouch: false,
      spaceBetween: 8,
    };
  }

  /**
   * Render swiper
   */
  private runRender(): void {
    this.flashsaleSection.style.display = "";

    this.swiper = new RootBannerSection(this.flashsaleSection);
    c.specialOffer.timerInit(".flash-sale__timer");
  }
}
